import { useEffect, useState, useRef, useMemo } from "react";
import { useSelector } from "react-redux";
import HlsJSPlayer from "./player.hls";

import './index.scss';
import { CONFIG } from "../../config/constant";
import { useLocation, useNavigate, useParams } from "react-router-dom";

const Player = (props) => {
  const params = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [playbackData, setPlaybackData] = useState(null);
  const [showControls, setShowControls] = useState(false);
  const [showSubtitle, setShowSubtitle] = useState(false);
  const [loader, setLoader] = useState(false);
  const [currentTime, setCurrentTime] = useState('00:00');
  const [duration, setDuration] = useState('00:00');
  const [playStatus, setPlayStatus] = useState(true);
  const [userPause, setUserPause] = useState(false);
  const [fullscreen, setFullscreen] = useState(false);
  const [progress, setProgress] = useState(0);
  const [subtitle, setSubtitle] = useState('off');
  const playerRef = useRef();
  const seekbarRef = useRef();
  const seekbarProgressRef = useRef();
  const playbtnRef = useRef();
  const mouseTracker = useRef(false);
  const seekTimeout = useRef(null);
  const auth = useSelector((state) => state.auth);
  const controlsTimeout = useRef(null);

  const isUrlAvailable = useMemo(() => {
    if (params.type == 'content') {
      return !!(playbackData?.content_url || playbackData?.content_url_mp4);
    }
    if (params.type == 'trailer') {
      return !!(playbackData?.trailer || playbackData?.trailer_mp4);
    }
  }, [playbackData])

  useEffect(() => {
    let selectedSubtitle = localStorage.getItem('selectedSubtitle');
    if (!selectedSubtitle) {
      selectedSubtitle = 'off';
    }
    setSubtitle(selectedSubtitle);
    getContentDetails();
    return () => {
      if (playerRef.current) {
        backClick();
      }
    }
  }, []);

  useEffect(() => {
    if (playbackData) {
      let url = playbackData?.content_url;
      if (params.type == 'trailer') {
        url = playbackData?.trailer;
      }
      if (playerRef.current) {
        playerRef.current?.destroy();
      }
      playerRef.current = new HlsJSPlayer({
        url,
        videoId: "video-player",
        preload: "auto",
        callback
      });
      playerRef.current?.init();
      // if (playbackData?.subtitle?.length > 0 && subtitle !== 'off') {
      //   setTextTrack(subtitle);
      // }
      // setDummySubtitle();
    }
  }, [playbackData]);

  const toHHMMSS = (time) => {
    if (!time) {
      return "00:00";
    }
    let sec_num = parseInt(time, 10);
    let hours = Math.floor(sec_num / 3600);
    let minutes = Math.floor((sec_num - hours * 3600) / 60);
    let seconds = sec_num - hours * 3600 - minutes * 60;
    if (hours < 10) {
      hours = "0" + hours;
    }
    if (minutes < 10) {
      minutes = "0" + minutes;
    }
    if (seconds < 10) {
      seconds = "0" + seconds;
    }
    if (hours > 0) {
      return hours + ":" + minutes + ":" + seconds;
    }
    if (hours == 0) {
      return minutes + ":" + seconds;
    }
  };

  async function getContentDetails(id) {
    setLoader(true);
    const url = CONFIG.BASE_URL + `app/video/${params?.id}`;
    let response = await fetch(url, {
      method: "GET",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
        "Authorization": auth?.token
      },
    });
    if (response && response.status === 200) {
      response = await response.json();
      setPlaybackData(response);
    } else {
      response = await response.json();
    }
  }

  async function updatePlayback() {
    const url = CONFIG.BASE_URL + `app/playback/update/${params?.id}`;
    let response = await fetch(url, {
      method: "GET",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "Content-Type": "application/json",
        "Authorization": auth?.token
      },
    });
    response = await response.json();
  }

  const controlVisibility = () => {
    if (!showControls) {
      setShowControls(true);
    }
    if (controlsTimeout?.current) {
      clearTimeout(controlsTimeout.current);
    }
    if (playerRef?.current?.isPaused() || showSubtitle) {
      return;
    }
    controlsTimeout.current = setTimeout(() => {
      setShowControls(false);
    }, 6000);
  };

  function enableDefaultSubtitle() {
    if (playbackData?.subtitle?.length > 0 && subtitle !== 'off') {
      setTextTrack(subtitle);
    }
  }

  function callback(event, ct, dt) {
    switch (event) {
      case 'LOADEDDATA':
        setDuration(toHHMMSS(dt));
        setShowControls(true);
        setLoader(false);
        updatePlayback();
        controlVisibility();
        enableDefaultSubtitle();
        break;
      case 'TIMEUPDATE':
        setProgress((ct / dt) * 100);
        setCurrentTime(toHHMMSS(ct));
        if (!duration) {
          setDuration(toHHMMSS(dt));
        }
        break;
      case 'PROGRESS':
        break;
      case "ENDED":
        setProgress(100);
        break;
    }
  }

  const playPauseHandler = () => {
    if (playerRef?.current?.isPaused()) {
      playerRef?.current?.play();
      setPlayStatus(true);
      setUserPause(false);
    } else {
      playerRef?.current?.pause();
      setPlayStatus(false);
      setUserPause(true);
    }
  };

  const rewind = (e) => {
    e.stopPropagation();
    if (playerRef?.current) {
      playerRef.current?.rewind(10);
    }
  };

  const forward = (e) => {
    e.stopPropagation();
    if (playerRef?.current) {
      playerRef.current?.forward(10);
    }
  };

  const isIOSSafari = () => {
    const ua = window.navigator.userAgent;
    const isIOS = /iP(ad|hone|od)/.test(ua);
    const isWebKit = /WebKit/.test(ua);
    const isChrome = /CriOS/.test(ua); // Chrome on iOS
    const isFirefox = /FxiOS/.test(ua); // Firefox on iOS
    return isIOS && isWebKit && !isChrome && !isFirefox;
  }

  const fullScreenHandler = (e) => {
    e.stopPropagation();
    if (!isIOSSafari()) {
      if (fullscreen) {
        setFullscreen(false);
        document.querySelector('.video-player').classList.remove('fullscreen');
      } else {
        setFullscreen(true);
        document.querySelector('.video-player').classList.add('fullscreen');
      }
    } else {
      if (fullscreen) {
        setFullscreen(false);
        document.exitFullscreen();
      } else {
        setFullscreen(true);
        document.documentElement.requestFullscreen();
      }
    }
  }

  const backClick = () => {
    playerRef.current?.destroy();
    const previousPath = location.state?.from || '/';
    navigate(previousPath);
  }

  const subtitleItemClick = (e, item) => {
    e.stopPropagation();
    if (item.language === 'off') {
      setSubtitle('off');
      localStorage.removeItem('selectedSubtitle');
      removeTextTrack();
    } else {
      setSubtitle(item.language);
      localStorage.setItem('selectedSubtitle', item.language);
      setTextTrack(item.language);
    }
    setShowSubtitle(false);
    controlVisibility();
  };

  const setDummySubtitle = () => {
    let track = document.createElement("track");
    track.id = "track";
    track.kind = "subtitles";
    track.src = 'https://credo-subtitles.s3.amazonaws.com/170142969653a85-742a8617a102267-2008a62a79961818.vtt';
    track.label = 'English';
    track.srclang = "en";
    track.type = "text/vtt";
    track.mode = "showing";
    track.default = true;
    playerRef.current?.playerDom?.appendChild(track);
  }

  const setTextTrack = (val) => {
    let tracks = playerRef.current?.playerDom?.querySelectorAll("track");
    tracks.forEach(track => track.remove());
    for (let i = 0; i < playbackData?.subtitle?.length; i++) {
      if (playbackData?.subtitle[i].language == val) {
        let track = document.createElement("track");
        track.id = "track";
        track.kind = "subtitles";
        track.src = playbackData?.subtitle[i]?.url;
        track.label = playbackData?.subtitle[i]?.label;
        track.srclang = playbackData?.subtitle[i].language;
        // track.type = "text/vtt";
        // track.mode = "showing";
        track.default = true;
        playerRef.current?.playerDom?.appendChild(track);
        const textTracks = playerRef.current?.playerDom?.textTracks;
        if (textTracks.length > 0) {
          textTracks[0].mode = 'showing';
        }
        break;
      }
    }
  };

  const removeTextTrack = () => {
    let tracks = document.querySelectorAll("track");
    for (let i = 0; i < tracks?.length; i++) {
      tracks?.[i]?.remove();
    }
  };

  const seekbarClick = (e) => {
    playerRef?.current?.pause();
    let clickPosition =
      ((e.clientX - e.currentTarget.getBoundingClientRect().left) /
        e.currentTarget.getBoundingClientRect().width) *
      100;
    let clickedTime =
      (parseInt(clickPosition, 10) * parseInt(playerRef?.current?.duration(), 10)) / 100;
    setProgress(parseInt(clickPosition, 10));
    playerRef?.current?.seek(clickedTime);
    if (!userPause) {
      playerRef?.current?.play();
    }
  };

  const onMouseEnterHandler = (e) => {
    mouseTracker.current = true;
  };

  const onMouseMoveHandler = (e) => {
    if (mouseTracker.current) {
      if (!playerRef?.current?.isPaused()) {
        playerRef?.current?.pause();
      }
      let clickPosition =
        ((e.clientX - e.currentTarget?.getBoundingClientRect().left) /
          e.currentTarget?.getBoundingClientRect().width) *
        100;
      let clickedTime =
        (parseInt(clickPosition, 10) * parseInt(playerRef?.current?.duration(), 10)) /
        100;
      seekbarProgressRef.current.style.width = `${clickPosition}%`;
      if (seekTimeout.current) {
        clearTimeout(seekTimeout.current);
      }
      seekTimeout.current = setTimeout(() => {
        playerRef?.current?.play();
        mouseTracker.current = false;
        playerRef?.current?.seek(clickedTime);
      }, 500);
    }
  };


  return (
    <>
      <div
        className="player-page"
        id="playerContainer"
        onMouseMove={controlVisibility}
        onTouchStart={controlVisibility}
        onClick={controlVisibility}
      >

        {showControls ? (
          <div className="player-header">
            <div className="player-header-wrapper">
              <span className="back-btn" onClick={backClick}></span>
              {/* <span className="player-title">{video_title || videoTitle}</span> */}
            </div>
            {/* {season_number && episode_number ? (
              <h6 className="sub-title">
                S{season_number}:EP{episode_number}
              </h6>
            ) : null} */}
          </div>
        ) : null}

        {showControls ? (
          <div className="player-footer">
            {/* {isSkipIntro ? (
              <div className="skip-intro" onClick={skipIntro}>
                Skip Intro
              </div>
            ) : null} */}
            {/* {isNextVideo ? (
              <div role="none" className="next-video" onClick={nextVideo}>
                {next_video.video_type == 2 ? "Next Episode" : "Next Movie"}
              </div>
            ) : null} */}
            <div className="seekbar-wrapper">
              <div className="current-timer">{currentTime}</div>
              <div
                role="none"
                className="seekbar"
                onClick={seekbarClick}
                ref={seekbarRef}
                onMouseMove={onMouseMoveHandler}
              >
                <div
                  className="seekbar-progress"
                  style={{ width: `${progress}%` }}
                  ref={seekbarProgressRef}
                >
                  <span
                    role="none"
                    className="seek-ball"
                    onMouseDown={onMouseEnterHandler}
                  ></span>
                </div>
              </div>
              <div className="remaining-timer">{duration}</div>
            </div>
            <div className="controls">
              <div className="control-left">
                {playStatus ? (
                  <span
                    className="play"
                    ref={playbtnRef}
                    onClick={playPauseHandler}
                  ></span>
                ) : (
                  <span className="pause" onClick={playPauseHandler}></span>
                )}
                <span className="rewind" onClick={rewind}></span>
                <span className="forward" onClick={forward}></span>
              </div>
              <div className="control-right">
                {playbackData?.subtitle?.length ? (
                  <span
                    className={`subtitle`}
                    onClick={() => { setShowSubtitle(!showSubtitle) }}
                  ></span>
                ) : null}
                <span
                  className={!fullscreen ? "fullscreen" : "exit-fullscreen"}
                  onClick={(e) => fullScreenHandler(e)}
                ></span>
              </div>
            </div>
            {showSubtitle ? <div className="subtitle-audio">
              <ul className="subtitle-list">
                <li
                  role="none"
                  className={`list-item ${subtitle === 'off' ? 'active' : ''}`}
                  key='off'
                  data-language='off'
                  onClick={(event) => subtitleItemClick(event, { language: 'off' })}
                >
                  <span className="check"></span>
                  <span className="text">Off</span>
                </li>
                {playbackData?.subtitle?.length > 0
                  ? playbackData?.subtitle?.map((item) => {
                    return (
                      <li
                        role="none"
                        className={`list-item ${subtitle === item.language ? 'active' : ''}`}
                        key={item?.label}
                        data-language={item?.language}
                        onClick={(event) => subtitleItemClick(event, item)}
                      >
                        <span className="check"></span>
                        <span className="text">{item?.label}</span>
                      </li>
                    );
                  })
                  : null}
              </ul>

            </div> : null}
          </div>
        ) : null}
        {loader && isUrlAvailable ? (
          <div className="loader">
            <span className="spinner"></span>
          </div>
        ) : null}
      </div>
      {!isUrlAvailable ? <div className="player-error">Unable to play content</div> : null}
    </>
  );
};

export default Player;
